import { IS_PWA } from 'constants/platform'
import React, { forwardRef, useEffect, useRef, useState } from 'react'
import { ScrollView, StyleSheet } from 'react-native'
import ReactPlayer from 'react-player'

import { UploadButton } from 'partials/UploadButton'

import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { Pressable } from 'widgets/Pressable'
import { Text } from 'widgets/Text'
import { VideoPlayer } from 'widgets/Video'

import { useMaxWidth } from 'hooks/useMaxWidth'

import commonStyles from 'utils/styles/commonStyles'
import { formatVideoDuration } from 'utils/time'

import { AllowedFileType, FileType, NativeFile } from 'types/file'

import { MediaPreview } from './widgets/MediaPreview'

interface Props {
  allowedFileType: AllowedFileType
  isAudioEnabledSwitches: boolean[]
  toggleIsAudioEnabled: (index: number) => void
  selectedFileTypes?: FileType[]
  selectedFileUrls?: string[]
  setFiles: ((file: NativeFile) => void) | ((file: React.ChangeEvent<HTMLInputElement>) => void)
  setFile: ((file: NativeFile) => void) | ((file: React.ChangeEvent<HTMLInputElement>) => void)
  setFileIndex: (index: number) => void
  size?: 'oneThird' | 'half' | 'twoThird' | 'full'
  deleteStepAtIndex: (stepIndex: number) => void
}

const fileTypeUiStringMap: { [key in Props['allowedFileType']]: string } = {
  image: 'image',
  video: 'video',
  both: 'media',
}

const styles = StyleSheet.create({
  selectedFileContainer: {
    maxWidth: 300,
    maxHeight: 400,
    overflow: 'hidden',
  },
})

export const MultipleMediaSelector = forwardRef(
  (
    {
      allowedFileType,
      isAudioEnabledSwitches,
      selectedFileTypes,
      selectedFileUrls,
      setFiles,
      setFile,
      setFileIndex,
      toggleIsAudioEnabled,
      size = 'oneThird',
      deleteStepAtIndex,
    }: Props,
    ref: any,
  ) => {
    const scrollViewRef = useRef(null)

    const { width: maxWidth } = useMaxWidth()

    const [scrollToEnd, setScrollToEnd] = useState(false)
    const [refresh, setRefresh] = useState(false)
    const [videoDurations, setVideoDurations] = useState(
      Array(selectedFileUrls ? selectedFileUrls.length : 0).fill(0),
    )

    const handleVideoLoaded = (index: number, videoPlayer: VideoPlayer) => {
      const allVideoDurations = videoDurations
      allVideoDurations[index] = videoPlayer?.getDuration()
      setVideoDurations(allVideoDurations)
      setRefresh(!refresh)
    }

    useEffect(() => {
      setVideoDurations(videoDurations)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refresh, videoDurations.length])

    useEffect(() => {
      if (!selectedFileUrls) {
        setVideoDurations([0])
      }
      setRefresh(!refresh)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFileUrls])

    const widthMultiplier = (): number => {
      switch (size) {
        case 'oneThird':
          return maxWidth * 0.3
        case 'half':
          return maxWidth * 0.45
        case 'twoThird':
          return maxWidth * 0.65
        case 'full':
          return maxWidth * 0.9
        default:
          return maxWidth
      }
    }

    const getChildComponent = (index: number) => {
      return (
        <Box margin={{ horizontal: 'sm', bottom: 'md' }} align='center' justifyContent='flex-start'>
          <Box
            align='center'
            justifyContent='center'
            borderRadius='sm'
            width={widthMultiplier()}
            height={widthMultiplier() * 1.2}
            border={
              selectedFileUrls === undefined || !selectedFileUrls[index]
                ? { width: 1, color: 'grey' }
                : undefined
            }
            backgroundColor={
              selectedFileUrls === undefined || !selectedFileUrls[index] ? 'greyLight' : 'black'
            }
            style={styles.selectedFileContainer}
          >
            {selectedFileUrls && selectedFileUrls[index] && (
              <Box
                zIndex={11}
                width='100%'
                height='100%'
                position='absolute'
                pointerEvents='box-none'
              >
                <Pressable onPress={() => deleteStepAtIndex(index)}>
                  <Box padding='xs'>
                    <Icon name='x' color='white' size={30} />
                  </Box>
                </Pressable>
              </Box>
            )}
            {(selectedFileUrls === undefined || !selectedFileUrls[index]) && (
              <Box zIndex={10} width='100%' height='100%' position='absolute'>
                <UploadButton
                  ref={ref}
                  multiple
                  fileType={allowedFileType}
                  onFileSelect={setFiles}
                />
              </Box>
            )}
            {selectedFileUrls && selectedFileUrls[index] && (
              <Box
                zIndex={11}
                width='100%'
                height='100%'
                position='absolute'
                pointerEvents='box-none'
                justifyContent='space-between'
                align='flex-end'
                flexDirection='row'
                padding='sm'
              >
                <Box // camera button
                  zIndex={11}
                  width={28}
                  height={28}
                  justifyContent='center'
                  align='center'
                  style={commonStyles.opacity90}
                >
                  <Icon name='camera' color='white' size={22} />
                  <Box position='absolute' width='100%' height='100%'>
                    <UploadButton
                      fileType={allowedFileType}
                      onFileSelect={(file) => {
                        setFileIndex(index)
                        setFile(file)
                      }}
                    />
                  </Box>
                </Box>
                {selectedFileTypes && selectedFileTypes[index] === 'video' && (
                  <Box height={28} align='center' justifyContent='center'>
                    <Text fontSize='sm' color='white'>
                      {formatVideoDuration(videoDurations[index])}
                    </Text>
                  </Box>
                )}

                {selectedFileTypes && selectedFileTypes[index] === 'video' && (
                  <Box // mute button
                    zIndex={11}
                    width={28}
                    height={28}
                    justifyContent='center'
                    align='center'
                    style={commonStyles.opacity90}
                  >
                    <Pressable onPress={() => toggleIsAudioEnabled(index)}>
                      <Icon
                        name={isAudioEnabledSwitches[index] ? 'volume-2' : 'volume-x'}
                        color='white'
                        size={22}
                      />
                    </Pressable>
                  </Box>
                )}
              </Box>
            )}
            <MediaPreview
              fileUrl={selectedFileUrls && selectedFileUrls[index]}
              isMuted={!isAudioEnabledSwitches[index]}
              placeholderContent={`Upload ${fileTypeUiStringMap[allowedFileType]}`}
              type={selectedFileTypes && selectedFileTypes[index]}
              handleVideoLoaded={(videoPlayer: VideoPlayer) =>
                handleVideoLoaded(index, videoPlayer)
              }
            />
          </Box>
        </Box>
      )
    }

    return (
      <Box align='center' justifyContent='flex-start' width='100%'>
        {/* This hack for PWAs allows us to load in uploads with working previews directly.
         * Without the attempt to load a player beforehand, the previews did not work until tapped in PWA mode.
         */}
        {IS_PWA && (
          <ReactPlayer
            url='https://res.cloudinary.com/upswingfitness-prod/video/upload/v1657911402/xlcsowbsrk13jimz4aru.mp4'
            width={0}
            height={0}
          />
        )}
        {(!selectedFileUrls?.length || !selectedFileTypes?.length) && getChildComponent(0)}
        <ScrollView
          ref={scrollViewRef}
          style={commonStyles.fullWidth}
          horizontal
          onContentSizeChange={() => {
            scrollToEnd && scrollViewRef.current?.scrollToEnd({ animated: true })
            setScrollToEnd(false)
          }}
        >
          {selectedFileTypes && selectedFileUrls?.map((url, index) => getChildComponent(index))}
        </ScrollView>
        {selectedFileUrls !== undefined && selectedFileUrls?.length !== 0 && (
          <Box
            width='100%'
            flexDirection='row'
            margin={{ vertical: 'md' }}
            justifyContent='center'
            align='center'
          >
            <Box flex={1} height={1} margin={{ right: 'md' }} backgroundColor='grey' />
            <Box
              width={40}
              height={40}
              align='center'
              justifyContent='center'
              borderRadius='xl'
              backgroundColor='v2primary'
            >
              <Icon name='plus' color='black' size={32} />
              <Box position='absolute' width='100%' height='100%'>
                <UploadButton
                  fileType={allowedFileType}
                  onFileSelect={(file) => {
                    setFileIndex(selectedFileUrls?.length ?? 0)
                    setFile(file)
                    setScrollToEnd(true)
                  }}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    )
  },
)
